import {
  computeMoneyBreakdown,
  percentOfAmount,
  renderInvoiceConcept,
  toCents,
  type MoneyBreakdown,
} from "./invoicingRules";
import {
  RECEIVABLE_STATUSES,
  type ReceivableStatus,
  type ReceivableSummary,
} from "./invoicingTypes";

/**
 * Almikia Epic E6 (TASK-040): pure rules of the invoicing screens (VoBo
 * approval preview, status totals, "Copiar concepto" availability). Money goes
 * through `invoicingRules` (integer cents, never float math).
 */

/** Statuses offered as filter chips on "Cuentas por cobrar" (a cancelled row stays reachable by search only). */
export const RECEIVABLE_FILTER_STATUSES = [
  "pendiente_vobo",
  "por_facturar",
  "facturada",
  "cobrada",
] as const satisfies readonly ReceivableStatus[];

const COPYABLE_STATUSES: readonly ReceivableStatus[] = [
  "por_facturar",
  "facturada",
  "cobrada",
];

/** The concept is frozen at VoBo, so it can be copied from `por_facturar` onward. */
export const canCopyConcept = (receivable: {
  status: ReceivableStatus;
  concept_text?: string | null;
}): boolean =>
  COPYABLE_STATUSES.includes(receivable.status) &&
  Boolean(receivable.concept_text);

/** Parses a typed decimal (`.` or `,` separator, no sign, up to `maxDecimals`); null when invalid or empty. */
export const parseDecimal = (text: string, maxDecimals = 2): number | null => {
  const normalized = text.trim().replace(",", ".");
  const pattern = new RegExp(`^\\d+(\\.\\d{1,${maxDecimals}})?$`);
  return pattern.test(normalized) ? Number(normalized) : null;
};

/** Sum of `total_amount` per status, added in integer cents. */
export const sumTotalsByStatus = (
  rows: ReadonlyArray<Pick<ReceivableSummary, "status" | "total_amount">>,
): Record<ReceivableStatus, number> => {
  const cents = Object.fromEntries(
    RECEIVABLE_STATUSES.map((status) => [status, 0]),
  ) as Record<ReceivableStatus, number>;
  for (const row of rows) cents[row.status] += toCents(row.total_amount);
  return Object.fromEntries(
    RECEIVABLE_STATUSES.map((status) => [status, cents[status] / 100]),
  ) as Record<ReceivableStatus, number>;
};

export type ApprovalDraft = {
  installmentText: string;
  pctText: string;
  /** null = the amount was not typed: it follows the percentage. */
  amountText: string | null;
  /** The concept typed by Dirección (corretaje receivables only). */
  conceptText: string;
};

export type PctSource = "agreement" | "edited" | "entered" | null;

export type ApprovalPreview = {
  installment: number | null;
  pct: number | null;
  amount: number | null;
  breakdown: MoneyBreakdown | null;
  concept: string | null;
  pctSource: PctSource;
  /** Keys under `crm.invoicing.approve.errors`, empty when the draft can be submitted. */
  errors: string[];
};

/** Developer-paid (BPO) receivables take the project template; the buyer-paid (corretaje) ones a typed concept. */
export const isDeveloperPaid = (receivable: ReceivableSummary): boolean =>
  receivable.payer_type === "developer";

/** The initial draft of the approval dialog: the receivable as generated. */
export const initialApprovalDraft = (
  receivable: ReceivableSummary,
): ApprovalDraft => ({
  installmentText: String(receivable.installment_number),
  pctText:
    receivable.commission_pct == null ? "" : String(receivable.commission_pct),
  amountText: null,
  conceptText: "",
});

/**
 * Live preview of an approval: installment, percentage, amount, IVA/total and
 * the concept the server will freeze (same renderer, same template). The
 * amount follows the percentage until it is typed explicitly.
 */
export const buildApprovalPreview = (
  receivable: ReceivableSummary,
  template: string | null,
  draft: ApprovalDraft,
): ApprovalPreview => {
  const errors: string[] = [];
  const installment = /^\d+$/.test(draft.installmentText.trim())
    ? Number(draft.installmentText.trim())
    : null;
  if (installment == null || installment < 1) errors.push("installment");

  const pct = parseDecimal(draft.pctText, 4);
  const developerPaid = isDeveloperPaid(receivable);
  if ((developerPaid && pct == null) || (pct != null && pct > 100)) {
    errors.push("pct");
  }

  let amount: number | null;
  if (draft.amountText != null) amount = parseDecimal(draft.amountText);
  else if (
    developerPaid &&
    receivable.sale_value != null &&
    pct != null &&
    pct !== receivable.commission_pct
  ) {
    amount = percentOfAmount(receivable.sale_value, pct);
  } else amount = receivable.subtotal;
  if (amount == null || amount <= 0) errors.push("amount");

  const source = developerPaid ? template : draft.conceptText.trim();
  if (!source) errors.push(developerPaid ? "template" : "concept");

  let pctSource: PctSource = null;
  if (pct != null) {
    if (receivable.commission_pct == null) pctSource = "entered";
    else pctSource = pct === receivable.commission_pct ? "agreement" : "edited";
  }

  return {
    installment,
    pct,
    amount,
    breakdown:
      amount != null && amount > 0
        ? computeMoneyBreakdown(amount, receivable.iva_pct)
        : null,
    concept:
      source && installment != null && installment >= 1
        ? renderInvoiceConcept(source, {
            installment,
            pct,
            unit: receivable.unit_identifier,
            client: receivable.client_name,
            advisor: receivable.advisor_name,
            project: receivable.project_name,
          })
        : null,
    pctSource,
    errors,
  };
};
